import { usePageMeta } from "@/hooks/usePageMeta";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

function AnimatedSection({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const { ref, isVisible } = useScrollAnimation(0.1);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateY(0)" : "translateY(30px)" }}
    >
      {children}
    </div>
  );
}

export default function Privacy() {
  usePageMeta({
    title: "מדיניות פרטיות | upsite",
    description: "מדיניות הפרטיות של אפסייט – כיצד אנו אוספים, משתמשים ומגנים על המידע האישי שלכם.",
  });

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh-strong" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#66CDAA]/10 rounded-full blur-[120px]" />
        <div className="container relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 font-[Rubik] leading-tight">
              מדיניות <span className="gradient-text">פרטיות</span>
            </h1>
            <p className="text-lg text-white/60 leading-relaxed">
              עודכן לאחרונה: מרץ 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24 section-navy">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto prose-custom space-y-10">

              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-[Rubik]">1. כללי</h2>
                <p className="text-white/60 leading-relaxed">
                  אפסייט בע״מ (ח.פ. 513651406) מתחייבת לשמור על פרטיותכם. מדיניות פרטיות זו מסבירה כיצד אנו אוספים, משתמשים, מאחסנים ומגנים על מידע אישי שנמסר לנו באמצעות אתר זה, בהתאם לחוק הגנת הפרטיות, תשמ"א-1981, ותיקון מס' 13 לחוק התקשורת (בזק ושידורים), תשמ"ב-1982.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-[Rubik]">2. איזה מידע אנו אוספים</h2>
                <p className="text-white/60 leading-relaxed mb-3">
                  אנו אוספים מידע שאתם מוסרים לנו מרצונכם באמצעות טפסי יצירת קשר באתר:
                </p>
                <ul className="list-disc list-inside space-y-2 text-white/60 leading-relaxed">
                  <li>שם מלא</li>
                  <li>כתובת דוא"ל</li>
                  <li>מספר טלפון</li>
                  <li>תוכן ההודעה שנשלחה</li>
                </ul>
                <p className="text-white/60 leading-relaxed mt-3">
                  בנוסף, אנו אוספים מידע טכני באמצעות כלי אנליטיקה (Google Analytics) ופיקסלים שיווקיים (Facebook Pixel), כגון: כתובת IP, סוג דפדפן, דפים שנצפו, זמן שהייה באתר ומקור ההגעה.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-[Rubik]">3. כיצד אנו משתמשים במידע</h2>
                <ul className="list-disc list-inside space-y-2 text-white/60 leading-relaxed">
                  <li>יצירת קשר וחזרה אליכם בתגובה לפנייתכם</li>
                  <li>שיפור השירות והתכנים באתר</li>
                  <li>שליחת מידע שיווקי ועדכונים – רק אם נתתם הסכמה מפורשת לכך</li>
                  <li>ניתוח סטטיסטי של השימוש באתר</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-[Rubik]">4. דיוור ופרסום (תיקון 13)</h2>
                <p className="text-white/60 leading-relaxed">
                  בהתאם לתיקון מס' 13 לחוק התקשורת, אנו לא נשלח אליכם דברי פרסומת או דיוור שיווקי ללא הסכמתכם המפורשת מראש. אם נתתם הסכמה, תוכלו לבטל אותה בכל עת באמצעות לחיצה על קישור "הסרה מרשימת תפוצה" בכל הודעה שתקבלו, או על ידי פנייה ישירה אלינו.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-[Rubik]">5. שיתוף מידע עם צדדים שלישיים</h2>
                <p className="text-white/60 leading-relaxed">
                  אנו לא מוכרים, משכירים או מעבירים את המידע האישי שלכם לצדדים שלישיים, למעט במקרים הבאים:
                </p>
                <ul className="list-disc list-inside space-y-2 text-white/60 leading-relaxed mt-3">
                  <li>ספקי שירות הפועלים מטעמנו (כגון שירותי אחסון ו-CRM) – אך ורק לצורך מתן השירות</li>
                  <li>כאשר נדרש על פי חוק או צו שיפוטי</li>
                  <li>להגנה על זכויותינו המשפטיות</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-[Rubik]">6. עוגיות (Cookies)</h2>
                <p className="text-white/60 leading-relaxed">
                  האתר משתמש בעוגיות לצורך תפעול תקין, ניתוח תנועה ושיפור חוויית הגלישה. ניתן לשנות את הגדרות העוגיות בדפדפן שלכם בכל עת, אך שימו לב שחסימת עוגיות עלולה לפגוע בחוויית השימוש באתר.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-[Rubik]">7. אבטחת מידע</h2>
                <p className="text-white/60 leading-relaxed">
                  אנו נוקטים אמצעי אבטחה סבירים ומקובלים כדי להגן על המידע האישי שלכם מפני גישה לא מורשית, שינוי, חשיפה או השמדה. עם זאת, אין שיטת אבטחה מושלמת ואנו לא יכולים להתחייב לאבטחה מוחלטת.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-[Rubik]">8. זכויותיכם</h2>
                <p className="text-white/60 leading-relaxed">
                  בהתאם לחוק הגנת הפרטיות, עומדות לכם הזכויות הבאות:
                </p>
                <ul className="list-disc list-inside space-y-2 text-white/60 leading-relaxed mt-3">
                  <li>לעיין במידע שנאסף עליכם</li>
                  <li>לבקש לתקן או למחוק את המידע שלכם</li>
                  <li>לבטל הסכמה לדיוור שיווקי בכל עת</li>
                  <li>לפנות אלינו בכל שאלה הנוגעת לפרטיותכם</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-[Rubik]">9. שינויים במדיניות</h2>
                <p className="text-white/60 leading-relaxed">
                  אנו עשויים לעדכן מדיניות זו מעת לעת. שינויים מהותיים יפורסמו באתר. המשך השימוש באתר לאחר עדכון המדיניות מהווה הסכמה לשינויים.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-[Rubik]">10. יצירת קשר</h2>
                <p className="text-white/60 leading-relaxed">
                  לכל שאלה, בקשה או תלונה בנושא פרטיות, ניתן לפנות אלינו:
                </p>
                <ul className="list-none space-y-2 text-white/60 leading-relaxed mt-3">
                  <li>אפסייט בע״מ – Upsite LTD</li>
                  <li>דוא"ל: info@upsite.co.il</li>
                  <li>טלפון: 054-295-0091</li>
                </ul>
              </div>

            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
